/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React from 'react';

import { useIntl } from 'react-intl';
import { graphql, useMutation } from 'react-relay';

import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import { SplitButton } from '@atlaskit/button/split-button/split-button';
import { cssMap, jsx } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { FinishMigrationReviewModal } from '@atlassian/company-hub-common/finish-migration-review-modal';
import type { MigrationReview } from '@atlassian/company-hub-common/types';

import type { migrationReviewActionBarPublishDraftHelpCenterChangesMutation } from './__generated__/migrationReviewActionBarPublishDraftHelpCenterChangesMutation.graphql';
import messages from './messages';

const styles = cssMap({
	container: {
		paddingBlockEnd: token('space.150'),
		paddingBlockStart: token('space.150'),
		paddingInlineEnd: token('space.300'),
		paddingInlineStart: token('space.300'),
	},
});

export const MigrationReviewActionBar = ({
	cloudId,
	helpCenterAri,
	helpCenterName,
	migrationReview,
}: {
	cloudId: string;
	helpCenterAri: string;
	helpCenterName: string;
	migrationReview: MigrationReview;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isFinishLaterModalOpen, setIsFinishLaterModalOpen] = React.useState(false);
	const [commitPublishDraftHelpCenterChanges, isPublishing] =
		useMutation<migrationReviewActionBarPublishDraftHelpCenterChangesMutation>(graphql`
			mutation migrationReviewActionBarPublishDraftHelpCenterChangesMutation(
				$cloudId: ID!
				$input: HelpCenterDraftPublishInput!
			) {
				helpCenter(cloudId: $cloudId) @optIn(to: "HelpCenterAggBeta") {
					publishDraftHelpCenterChanges(input: $input) {
						success
						errors {
							message
						}
					}
				}
			}
		`);

	const handlePublish = () => {
		if (isPublishing) {
			return;
		}

		commitPublishDraftHelpCenterChanges({
			variables: {
				cloudId,
				input: { helpCenterAri },
			},
			onCompleted: () => {},
			onError: () => {},
		});
	};

	const handleFinishLaterConfirm = () => {
		migrationReview.onFinishLater();
		setIsFinishLaterModalOpen(false);
	};

	return (
		<React.Fragment>
			<Box backgroundColor="color.background.accent.blue.subtlest" xcss={styles.container}>
				<Inline
					alignBlock="center"
					rowSpace="space.150"
					shouldWrap
					space="space.200"
					spread="space-between"
					testId="migration-review-action-bar"
				>
					<Inline alignBlock="center" space="space.100">
						<EyeOpenIcon label="" />
						<Text>{formatMessage(messages.banner)}</Text>
					</Inline>
					<Inline
						alignBlock="center"
						rowSpace="space.100"
						shouldWrap
						space="space.100"
						testId="migration-review-action-bar-actions"
					>
						<SplitButton appearance="primary">
							<Button isDisabled={isPublishing} isLoading={isPublishing} onClick={handlePublish}>
								{formatMessage(messages.publish)}
							</Button>
							<DropdownMenu<HTMLButtonElement>
								shouldRenderToParent
								trigger={({ triggerRef, ...triggerProps }) => (
									<IconButton
										ref={triggerRef}
										{...triggerProps}
										icon={ChevronDownIcon}
										label={formatMessage(messages.morePublishActions)}
									/>
								)}
							>
								<DropdownItemGroup>
									<DropdownItem onClick={migrationReview.onEditDraft}>
										{formatMessage(messages.editDraft)}
									</DropdownItem>
									<DropdownItem onClick={() => setIsFinishLaterModalOpen(true)}>
										{formatMessage(messages.finishLater)}
									</DropdownItem>
								</DropdownItemGroup>
							</DropdownMenu>
						</SplitButton>
					</Inline>
				</Inline>
			</Box>
			<FinishMigrationReviewModal
				helpCenterName={helpCenterName}
				isOpen={isFinishLaterModalOpen}
				onCancel={() => setIsFinishLaterModalOpen(false)}
				onConfirm={handleFinishLaterConfirm}
			/>
		</React.Fragment>
	);
};
